import { useState, useEffect, useCallback } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, BarChart3 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (val) => {
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
  if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
  if (val >= 1000) return `₹${(val / 1000).toFixed(0)}K`;
  return `₹${val}`;
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl border border-slate-700">
      <p className="font-semibold text-sm mb-1">{d.label}</p>
      <p className="text-blue-300">{d.orders} orders</p>
      <p className="text-emerald-300">Med: {formatCurrency(d.medicine_cost)}</p>
      <p className="text-amber-300">Ship: {formatCurrency(d.shipping_cost)}</p>
      <p className="text-white font-medium border-t border-slate-600 mt-1 pt-1">Total: {formatCurrency(d.total_cost)}</p>
    </div>
  );
};

const MonthlyChart = ({ dateFrom, dateTo, token, onDayClick }) => {
  const [data, setData] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState(null);

  const fetchData = useCallback(async () => {
    if (!dateFrom || !dateTo || !token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("date_from", dateFrom);
      params.set("date_to", dateTo);
      const res = await fetch(`${BACKEND_URL}/api/shipment-costing/daily-summary?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json();
        setData(json.daily_data || []);
      }
    } catch (e) {
      console.error("Chart fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!dateFrom || !dateTo) return null;

  const maxOrders = Math.max(...data.map((d) => d.orders), 1);

  return (
    <div data-testid="monthly-chart-container" className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      {/* Toggle Header */}
      <button
        data-testid="monthly-chart-toggle"
        onClick={() => setCollapsed((p) => !p)}
        className="w-full flex items-center justify-between px-4 py-2 bg-slate-50 hover:bg-slate-100 transition-colors"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <BarChart3 className="w-4 h-4 text-blue-600" />
          Daily Overview
          {data.length > 0 && (
            <span className="text-xs text-slate-500 font-normal">
              {data.length} day{data.length !== 1 ? "s" : ""} · {data.reduce((s, d) => s + d.orders, 0)} orders
            </span>
          )}
        </span>
        {collapsed ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronUp className="w-4 h-4 text-slate-400" />}
      </button>

      {/* Chart Body */}
      {!collapsed && (
        <div className="px-3 py-2">
          {loading ? (
            <div className="h-28 flex items-center justify-center text-sm text-slate-400">Loading chart...</div>
          ) : data.length === 0 ? (
            <div className="h-20 flex items-center justify-center text-sm text-slate-400">No data for this period</div>
          ) : (
            <ResponsiveContainer width="100%" height={120}>
              <BarChart
                data={data}
                margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: "#94a3b8" }}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickLine={false}
                  interval={data.length > 15 ? Math.floor(data.length / 10) : 0}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(59,130,246,0.08)" }} />
                <Bar
                  dataKey="orders"
                  radius={[3, 3, 0, 0]}
                  cursor="pointer"
                  onClick={(entry) => onDayClick?.(entry.date)}
                  onMouseEnter={(_, idx) => setHoveredIdx(idx)}
                >
                  {data.map((entry, idx) => {
                    const intensity = 0.35 + (entry.orders / maxOrders) * 0.65;
                    const isHovered = hoveredIdx === idx;
                    return (
                      <Cell
                        key={idx}
                        fill={isHovered ? `rgba(37,99,235,${Math.min(intensity + 0.15, 1)})` : `rgba(59,130,246,${intensity})`}
                      />
                    );
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      )}
    </div>
  );
};

export default MonthlyChart;
